export interface Node {
	id: number;
	x: number;
	y: number;
}

export interface Edge {
	from: number;
	to: number;
}

export const getRandomNodes = (): Node[] => {
	const nodeCount = Math.floor(Math.random() * 3) + 4;
	let nodes: Node[];
	let edges: Edge[];
	let bestNodes: Node[] = [];
	let maxIntersections = 0;

	do {
		for (let attempt = 0; attempt < 30; attempt++) {
			nodes = Array.from({ length: nodeCount }, (_, i) => ({
				id: i + 1,
				x: Math.random() * 890 + 25,
				y: Math.random() * 340 + 200,
			}));
			edges = getPredefinedEdges(nodeCount);

			const intersections = countIntersections(nodes, edges);
			if (intersections > maxIntersections) {
				maxIntersections = intersections;
				bestNodes = [...nodes];
			}
		}
	} while (maxIntersections === 0);

	return bestNodes;
};

const countIntersections = (nodes: Node[], edges: Edge[]): number => {
	let intersectionCount = 0;

	for (let i = 0; i < edges.length; i++) {
		const edgeA = edges[i];
		const fromA = nodes.find((n) => n.id === edgeA.from);
		const toA = nodes.find((n) => n.id === edgeA.to);
		if (!fromA || !toA) continue;

		for (let j = i + 1; j < edges.length; j++) {
			const edgeB = edges[j];
			const fromB = nodes.find((n) => n.id === edgeB.from);
			const toB = nodes.find((n) => n.id === edgeB.to);
			if (!fromB || !toB) continue;

			if (
				getIntersection(
					fromA.x,
					fromA.y,
					toA.x,
					toA.y,
					fromB.x,
					fromB.y,
					toB.x,
					toB.y,
				)
			) {
				intersectionCount++;
			}
		}
	}
	return intersectionCount;
};

const predefinedEdges: Record<number, Edge[][]> = {
	4: [
		[
			{ from: 1, to: 2 },
			{ from: 1, to: 3 },
			{ from: 2, to: 3 },
			{ from: 4, to: 1 },
			{ from: 4, to: 2 },
			{ from: 4, to: 3 },
		],
	],
	5: [
		[
			{ from: 1, to: 3 },
			{ from: 1, to: 2 },
			{ from: 1, to: 4 },
			{ from: 2, to: 3 },
			{ from: 2, to: 5 },
			{ from: 3, to: 4 },
			{ from: 3, to: 5 },
			{ from: 4, to: 5 },
		],
		[
			{ from: 1, to: 2 },
			{ from: 1, to: 3 },
			{ from: 2, to: 3 },
			{ from: 3, to: 4 },
			{ from: 3, to: 5 },
			{ from: 4, to: 5 },
		],
	],
	6: [
		[
			{ from: 1, to: 2 },
			{ from: 1, to: 3 },
			{ from: 2, to: 4 },
			{ from: 3, to: 4 },
			{ from: 5, to: 6 },
			{ from: 5, to: 1 },
			{ from: 5, to: 2 },
			{ from: 6, to: 3 },
			{ from: 6, to: 4 },
		],
		[
			{ from: 1, to: 2 },
			{ from: 1, to: 3 },
			{ from: 2, to: 3 },
			{ from: 3, to: 4 },
			{ from: 4, to: 5 },
			{ from: 4, to: 6 },
			{ from: 5, to: 6 },
		],
	],
};

export const getPredefinedEdges = (nodeCount: number): Edge[] => {
	const edgesList = predefinedEdges[nodeCount];
	return edgesList[Math.floor(Math.random() * edgesList.length)];
};

export const getIntersection = (
	ax: number,
	ay: number,
	bx: number,
	by: number,
	cx: number,
	cy: number,
	dx: number,
	dy: number,
) => {
	const EPSILON = 1;
	const det = (bx - ax) * (dy - cy) - (by - ay) * (dx - cx);
	if (det === 0) return null;

	const lambda = ((dy - cy) * (dx - ax) + (cx - dx) * (dy - ay)) / det;
	const gamma = ((ay - by) * (dx - ax) + (bx - ax) * (dy - ay)) / det;

	if (lambda > 0 && lambda < 1 && gamma > 0 && gamma < 1) {
		const x = ax + lambda * (bx - ax);
		const y = ay + lambda * (by - ay);

		const distToStart1 = Math.hypot(x - ax, y - ay);
		const distToEnd1 = Math.hypot(x - bx, y - by);
		const distToStart2 = Math.hypot(x - cx, y - cy);
		const distToEnd2 = Math.hypot(x - dx, y - dy);

		if (
			distToStart1 > EPSILON &&
			distToEnd1 > EPSILON &&
			distToStart2 > EPSILON &&
			distToEnd2 > EPSILON
		) {
			return { x, y };
		}
	}

	return null;
};

export const isGameSolved = (nodes: Node[], edges: Edge[]) => {
	for (let i = 0; i < edges.length; i++) {
		const edgeA = edges[i];
		const fromA = nodes.find((n) => n.id === edgeA.from);
		const toA = nodes.find((n) => n.id === edgeA.to);
		if (!fromA || !toA) continue;

		for (let j = i + 1; j < edges.length; j++) {
			const edgeB = edges[j];
			const fromB = nodes.find((n) => n.id === edgeB.from);
			const toB = nodes.find((n) => n.id === edgeB.to);
			if (!fromB || !toB) continue;

			if (
				getIntersection(
					fromA.x,
					fromA.y,
					toA.x,
					toA.y,
					fromB.x,
					fromB.y,
					toB.x,
					toB.y,
				)
			) {
				return false;
			}
		}
	}
	return true;
};
